import { useState, useRef, useEffect, useMemo } from "react";
import { Copy, Check, Crown, Play, Plus, X, Sparkles, Vote, Trash2, LogOut, Save, Download, Search } from "lucide-react";
import { toast } from "sonner";
import { AdBanner, SupportPanel } from "../components/Payments";
import { useT } from "../lib/i18n";
import { groupedDecks } from "../lib/decks";
import { GoogleAd } from "../components/Ads";
import { LegalFooter } from "./LegalFooter";

const safeArray = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  return Object.values(val);
};

const normalizeText = (text) => {
  if (!text) return "";
  return text.toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]/g, "");
};

const calculateDistance = (a, b) => {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const matrix = [];
  for (let i = 0; i <= b.length; i++) matrix[i] = [i];
  for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
      }
    }
  }
  return matrix[b.length][a.length];
};

function FlatDeckCard({ deck, room, playerId, actions, addingSets, setAddingSets, validSets }) {
  const { t } = useT();
  const activeSet = safeArray(validSets).find(s => s.id === deck.id);
  const cardCount = safeArray(deck.cards).length;
  const voters = activeSet ? safeArray(room?.votes?.[activeSet.id]) : [];
  const isVoted = voters.includes(playerId);

  const handleClick = (e) => {
    e.preventDefault();
    if (activeSet) {
      actions.vote(activeSet.id);
    } else {
      if (addingSets[deck.id]) return;
      setAddingSets(prev => ({ ...prev, [deck.id]: true }));
      const formattedCards = safeArray(deck.cards).map(term => ({ 
         _id: (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`, 
         ...term,
         text: term.en || term.pl || (typeof term === 'string' ? term : term.text || String(term)), 
         description: "", link: "" 
      }));
      const combinedName = `${deck.groupName} - ${deck.subName}`;
      actions.addSet(deck.id, formattedCards, combinedName).then(() => actions.vote(deck.id));
      setTimeout(() => setAddingSets(prev => ({ ...prev, [deck.id]: false })), 1000);
    }
  };

  return (
    <button onClick={handleClick} disabled={addingSets[deck.id] && !activeSet} className={`flex items-center justify-between p-3.5 rounded-2xl border-2 transition-all ${isVoted ? "border-[#FFE800] bg-[#FFE800]/10 shadow-[0_0_15px_rgba(255,232,0,0.15)]" : "border-white/5 bg-[#12121A] hover:border-white/20"}`}>
       <div className="flex items-center gap-3 text-left">
          <span className="text-2xl">{deck.emoji}</span>
          <div className="flex flex-col">
             <span className="font-bold text-sm text-white">{deck.groupName}</span>
             <span className="text-[11px] text-[#00FFFF] font-bold tracking-wider">{deck.subName}</span>
          </div>
       </div>
       <div className="flex flex-col items-end">
          <div className={`text-xs font-bold ${isVoted ? "text-[#FFE800]" : "text-white/30"}`}>
             {voters.length} ✓
          </div>
          <span className="text-[9px] text-white/30 tracking-widest uppercase mt-0.5">{cardCount} {t("cards_word") || "kart"}</span>
       </div>
    </button>
  );
}

function VoteCard({ s, room, playerId, actions }) {
  const { t } = useT();
  const voters = safeArray(room?.votes?.[s.id]);
  const isVoted = voters.includes(playerId);
  const cardCount = safeArray(s.cards).length;
  const [flash, setFlash] = useState(false);
  const prevVotesRef = useRef(voters.length);
  const displayName = s.name || s.id;

  useEffect(() => {
    if (voters.length > prevVotesRef.current) {
      if (!isVoted) {
        setFlash(true);
        const timer = setTimeout(() => setFlash(false), 800);
        return () => clearTimeout(timer);
      }
    }
    prevVotesRef.current = voters.length;
  }, [voters.length, isVoted]);

  return (
    <button onClick={() => actions.vote(s.id)} className={`flex flex-col items-start gap-3 w-full text-left p-4 rounded-2xl border-2 transition-all duration-300 relative overflow-hidden ${isVoted ? "border-[#FFE800] bg-[#FFE800]/10" : flash ? "border-[#FFE800] bg-[#FFE800]/30 shadow-[0_0_20px_rgba(255,232,0,0.5)] scale-[1.02]" : "border-white/10 bg-[#12121A] hover:border-white/30"}`}>
      <div className={`absolute inset-0 bg-[#FFE800]/20 transition-opacity duration-500 pointer-events-none ${flash ? 'opacity-100' : 'opacity-0'}`} />
      <div className="font-display font-bold text-lg relative z-10">{displayName}</div>
      <div className="flex items-center gap-4 text-xs font-mono2 tracking-widest uppercase relative z-10">
        <div className="flex flex-col items-center">
          <span className={isVoted ? "text-white/70" : "text-white/40"}>{cardCount}</span>
          <span className={`text-[10px] ${isVoted ? "text-white/50" : "text-white/30"}`}>{t("cards_word") || "KART"}</span>
        </div>
        <span className="text-white/20">·</span>
        <div className={`flex flex-col items-center ${isVoted || flash ? "text-[#FFE800]" : "text-white/40"}`}>
          <span className={`font-bold text-base leading-none ${isVoted || flash ? "text-[#FFE800]" : "text-white/40"}`}>{voters.length}</span>
          <span className={`text-[10px] ${isVoted || flash ? "text-[#FFE800]/80" : "text-white/30"}`}>{t("votes_word") || "GŁOSÓW"}</span>
        </div>
      </div>
    </button>
  );
}

export default function Lobby({ room: initialRoom, playerId, actions }) {
  const { t } = useT();
  const [room, setRoom] = useState(initialRoom);
  const [customTime, setCustomTime] = useState(""); 
  const [customCards, setCustomCards] = useState(""); 
  const [searchQuery, setSearchQuery] = useState("");
  
  const [savedLocalSets, setSavedLocalSets] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('my_custom_sets') || '[]');
    } catch {
      return [];
    }
  });
  
  useEffect(() => { setRoom(initialRoom); }, [initialRoom]);

  useEffect(() => {
    if (playerId === initialRoom?.host_id && actions.getRoom) {
      const intervalId = setInterval(async () => {
        try {
          const res = await actions.getRoom();
          if (res && res.room) {
            setRoom(prev => {
              const getLight = (r) => ({
                 p: safeArray(r?.players).length,
                 v: r?.votes,
                 m: r?.mode,
                 t: r?.turn_seconds,
                 c: r?.cards_per_player,
                 s: safeArray(r?.sets).length
              });
              if (JSON.stringify(getLight(prev)) !== JSON.stringify(getLight(res.room))) return res.room;
              return prev;
            });
          }
        } catch (e) {}
      }, 500);
      return () => clearInterval(intervalId);
    }
  }, [playerId, initialRoom?.host_id, actions]);

  const handleLeave = async () => {
      const updatedPlayers = safeArray(room?.players).filter(p => p.id !== playerId);
      if (actions.updateRoom) await actions.updateRoom({ ...room, players: updatedPlayers });
      if (actions.leave) actions.leave();
  };

  const isHost = playerId === room?.host_id;
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [setName, setSetName] = useState("");
  const newCard = () => ({ _id: (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`, text: "", description: "", link: "" });
  const [cards, setCards] = useState([newCard()]);
  const [addingSets, setAddingSets] = useState({});

  const copyCode = () => {
    navigator.clipboard.writeText(room?.code || "");
    setCopied(true);
    toast.success(t("copied"));
    setTimeout(() => setCopied(false), 1500);
  };

  const saveSet = async () => {
    const valid = cards.filter((c) => c.text.trim());
    if (valid.length < 3) return toast.error(t("add_3_cards") || "Dodaj min. 3 karty");
    const setId = `custom-${Date.now()}`;
    await actions.addSet(setName, valid).then(() => actions.vote(setName));
    
    const newSavedSets = [...savedLocalSets, { id: setId, name: setName, cards: valid }];
    setSavedLocalSets(newSavedSets);
    localStorage.setItem('my_custom_sets', JSON.stringify(newSavedSets));
    
    setCreating(false);
    setSetName("");
    setCards([newCard()]);
    toast.success(t("custom_added") || "Zestaw zapisany i dodany do gry!");
  };

  const deleteLocalSet = (setId) => {
     const updated = savedLocalSets.filter(s => s.id !== setId);
     setSavedLocalSets(updated);
     localStorage.setItem('my_custom_sets', JSON.stringify(updated));
  };

  const updateCard = (i, k, v) => setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)));
  const supportRef = useRef(null);
  
  const modes = [
    { id: "fcfs", name: t("fcfs_name") || "Kto pierwszy", desc: t("fcfs_desc") || "Wygrywa szybkość." },
    { id: "podium", name: t("podium_name") || "Podium", desc: t("podium_desc") || "Kolejkowe zgadywanie." },
    { id: "ofa", name: t("ofa_name") || "Jeden za wszystkich", desc: t("ofa_desc") || "Wspólny czas, podpowiedzi i dynamiczna zmiana ról." },
  ];

  const currentMode = room?.mode || "fcfs";
  const currentTurnSeconds = room?.turn_seconds || 60;
  const currentCards = room?.cards_per_player || (currentMode === 'ofa' ? 2 : 3);

  const timeOpts = currentMode === "ofa" 
    ? [{ label: "60s", val: 60 }, { label: "120s", val: 120 }, { label: "180s", val: 180 }]
    : [{ label: "30s", val: 30 }, { label: "60s", val: 60 }, { label: "90s", val: 90 }];

  const cardOpts = currentMode === "ofa" ? [2] : [3, 6, 9];

  let showAds = true;
  if (room?.ad_free_until) {
    if (room.ad_free_until === true) showAds = false;
    else {
      let ms = 0;
      if (room.ad_free_until.seconds) ms = room.ad_free_until.seconds * 1000;
      else if (typeof room.ad_free_until === "number") ms = room.ad_free_until < 10000000000 ? room.ad_free_until * 1000 : room.ad_free_until;
      else ms = new Date(room.ad_free_until).getTime();
      if (ms > Date.now()) showAds = false;
    }
  }

  const handleStartGame = async () => {
     if (actions.config) await actions.config({ card_index: Math.floor(Math.random() * 1000) });
     actions.start();
  };

  const allAvailableDecks = useMemo(() => {
     const list = [];
     Object.entries(groupedDecks || {}).forEach(([gKey, gData]) => {
         Object.entries(gData.subdecks || {}).forEach(([sKey, sData]) => {
             list.push({
                 id: sKey,
                 groupKey: gKey,
                 groupName: gData.name || "Kategoria",
                 subName: sData.name || "Zestaw",
                 emoji: gData.emoji || "🃏",
                 cards: sData.cards,
                 searchString: normalizeText(`${gData.name} ${sData.name}`)
             });
         });
     });
     return list;
  }, []);

  const popularDecks = useMemo(() => {
      const picked = [];
      const seenGroups = new Set();
      for (const deck of allAvailableDecks) {
          if (!seenGroups.has(deck.groupKey)) {
              picked.push(deck);
              seenGroups.add(deck.groupKey);
              if (picked.length >= 6) break;
          }
      }
      return picked;
  }, [allAvailableDecks]);

  const searchResults = useMemo(() => {
     if (!searchQuery.trim()) return [];
     const query = normalizeText(searchQuery);
     
     const results = allAvailableDecks.map(deck => {
        if (deck.searchString.includes(query)) return { ...deck, score: 0 };
        
        const words = deck.searchString.split(" ");
        let bestDist = 999;
        words.forEach(w => {
            const dist = calculateDistance(query, w);
            if (dist < bestDist) bestDist = dist;
        });
        
        const fullDist = calculateDistance(query, deck.searchString);
        bestDist = Math.min(bestDist, fullDist);
        
        return { ...deck, score: bestDist };
     });

     const threshold = query.length <= 4 ? 1 : 3;
     
     return results
        .filter(r => r.score <= threshold)
        .sort((a, b) => a.score - b.score)
        .slice(0, 12);
  }, [searchQuery, allAvailableDecks]);

  const hasSelectedDecks = Object.values(room?.votes || {}).some(voters => safeArray(voters).length > 0);
  const canStart = (safeArray(room?.players).length >= 2) && hasSelectedDecks;

  const validSets = safeArray(room?.sets).filter(s => s && s.id && s.name);
  const allSubdeckIds = allAvailableDecks.map(d => d.id);
  const customSets = validSets.filter(s => !allSubdeckIds.includes(s.id));

  return (
    <div className="grain min-h-screen bg-[#050510] text-white flex flex-col">
      <div className={`flex-1 grid grid-cols-1 ${showAds ? "lg:grid-cols-[auto_1fr_auto]" : ""} gap-4 sm:gap-6 p-4 sm:p-6 w-full max-w-[1920px] mx-auto`}>
        {showAds && (
          <div className="hidden lg:block w-48 xl:w-64">
            <div className="sticky top-4 h-[calc(100vh-32px)]">
              <GoogleAd format="vertical" minHeight={600} label="ad" className="h-full w-full" />
            </div>
          </div>
        )}
        <div className="w-full max-w-4xl mx-auto flex flex-col relative z-10">
          <div className="mb-8 space-y-4">
            <AdBanner adFreeUntil={room?.ad_free_until} onRemove={() => supportRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })} />
            {showAds && <GoogleAd format="horizontal" label="ad" className="w-full" />}
          </div>
          
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">{t("lobby")}</h1>
            <div className="flex items-center gap-3">
              <button onClick={copyCode} className="flex items-center gap-3 px-5 py-3 rounded-2xl bg-[#12121A] border-2 border-[#00FFFF]/40 hover:border-[#00FFFF] transition-colors">
                <span className="font-mono2 text-xs tracking-[0.2em] text-[#A0A0B0]">{t("code")}</span>
                <span className="font-display text-2xl tracking-[0.3em] text-[#00FFFF]">{room?.code || ""}</span>
                {copied ? <Check size={20} className="text-[#00FF66]" /> : <Copy size={20} />}
              </button>
              <button onClick={handleLeave} className="flex items-center gap-2 px-4 py-3 rounded-2xl border-2 border-white/15 text-[#A0A0B0] hover:border-[#FF007F] hover:text-[#FF007F] transition-colors font-bold text-sm">
                <LogOut size={18} /> {t("leave")}
              </button>
            </div>
          </div>

          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6 lg:gap-10">
            <div className="space-y-8">
              <div>
                <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] mb-4">{t("players")} · {safeArray(room?.players).length}</h2>
                <div className="space-y-3">
                  {safeArray(room?.players).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 p-3.5 rounded-2xl bg-[#12121A] border-2 border-white/10">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center font-display font-black text-[#050510]" style={{ backgroundColor: p.color || "#FFF" }}>
                        {(p.name || "?").charAt(0).toUpperCase()}
                      </div>
                      <span className="font-bold">{p.name || "?"}</span>
                      {p.id === room?.host_id && <Crown size={16} className="text-[#FFE800] ml-auto" />}
                      {p.id === playerId && <span className="text-xs text-[#505060] ml-auto">{t("you")}</span>}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] mb-3">{t("game_mode")}</h2>
                <div className="grid grid-cols-1 gap-3">
                  {modes.map((m) => (
                    <button 
                      key={m.id} 
                      disabled={!isHost} 
                      onClick={() => actions.config({ mode: m.id })} 
                      className={`text-left p-4 rounded-2xl border-2 transition-all ${currentMode === m.id ? "border-[#FF007F] bg-[#FF007F]/10" : "border-white/10 bg-[#12121A]"} ${isHost && currentMode !== m.id ? "hover:border-white/30" : ""} ${!isHost ? "cursor-default" : ""}`}
                    >
                      <div className="font-display font-bold">{m.name}</div>
                      <div className="text-xs text-[#A0A0B0] mt-1">{m.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {(currentMode === "podium" || currentMode === "ofa") && (
                <div>
                  <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] mb-3">{t("cards_per_player") || "Liczba kart na gracza"}</h2>
                  <div className="grid grid-cols-4 gap-2 sm:gap-3">
                    {cardOpts.map((c) => {
                      const isActive = currentCards === c;
                      return (
                        <button
                          key={c}
                          disabled={!isHost}
                          onClick={() => {
                             actions.config({ cards_per_player: c });
                             setCustomCards("");
                          }}
                          className={`py-3 rounded-2xl border-2 font-display font-bold text-center transition-all ${
                            isActive 
                              ? "border-[#00FFFF] bg-[#00FFFF]/10 text-[#00FFFF]" 
                              : "border-white/10 bg-[#12121A] text-[#A0A0B0]"
                          } ${isHost && !isActive ? "hover:border-white/30 hover:text-white hover:scale-[1.02]" : ""} ${!isHost ? "cursor-default opacity-80" : ""}`}
                        >
                          {c}
                        </button>
                      );
                    })}
                    
                    <input 
                      type="number" 
                      placeholder={t("custom_amount") || "Inna ilość"} 
                      disabled={!isHost}
                      value={!cardOpts.includes(currentCards) ? currentCards : customCards}
                      onChange={(e) => {
                        setCustomCards(e.target.value);
                        const val = parseInt(e.target.value);
                        if (val > 0) actions.config({ cards_per_player: val });
                      }}
                      className={`${currentMode === 'ofa' ? 'col-span-3' : 'w-full'} py-3 rounded-2xl border-2 font-display font-bold text-center transition-all bg-[#12121A] focus:outline-none ${
                        !cardOpts.includes(currentCards)
                          ? "border-[#00FFFF] text-[#00FFFF] bg-[#00FFFF]/10" 
                          : "border-white/10 text-[#A0A0B0] focus:border-white/30"
                      } ${!isHost ? "cursor-default opacity-80" : ""}`}
                    />
                  </div>
                </div>
              )}

              <div>
                <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] mb-3">
                  {currentMode === 'ofa' ? (t("time_per_card_ofa") || "Czas na kartę (s)") : (t("time_limit") || "Czas tury (s)")}
                </h2>
                {currentMode === "ofa" && !timeOpts.find(o => o.val === currentTurnSeconds) && !customTime ? (
                  <div className="p-4 rounded-2xl border-2 border-[#B26DFF] bg-[#B26DFF]/10 text-center mb-3">
                    <span className="font-display font-bold text-[#B26DFF] text-xl">
                      {t("auto_time") || "Automatyczny:"} {Math.max(0, (safeArray(room?.players).length - 1) * 120)}s
                    </span>
                    <p className="text-xs text-[#A0A0B0] mt-1">{t("ofa_time_desc") || "2 karty x 60s dla każdego zgadującego"}</p>
                  </div>
                ) : null}

                <div className="grid grid-cols-4 gap-2 sm:gap-3">
                  {timeOpts.map((tOpt) => {
                    const isActive = !room?.infinite && currentTurnSeconds === tOpt.val;
                    return (
                      <button
                        key={tOpt.label}
                        disabled={!isHost}
                        onClick={() => {
                           actions.config({ turn_seconds: tOpt.val, infinite: false });
                           setCustomTime("");
                        }}
                        className={`py-3 rounded-2xl border-2 font-display font-bold text-center transition-all ${
                          isActive 
                            ? "border-[#00FFFF] bg-[#00FFFF]/10 text-[#00FFFF]" 
                            : "border-white/10 bg-[#12121A] text-[#A0A0B0]"
                        } ${isHost && !isActive ? "hover:border-white/30 hover:text-white hover:scale-[1.02]" : ""} ${!isHost ? "cursor-default opacity-80" : ""}`}
                      >
                        {tOpt.label}
                      </button>
                    );
                  })}
                  
                  <input 
                    type="number" 
                    placeholder={t("custom_amount") || "Inny (s)"} 
                    disabled={!isHost}
                    value={!room?.infinite && !timeOpts.find(o => o.val === currentTurnSeconds) ? currentTurnSeconds : customTime}
                    onChange={(e) => {
                      setCustomTime(e.target.value);
                      const val = parseInt(e.target.value);
                      if (val > 0) actions.config({ turn_seconds: val, infinite: false });
                    }}
                    className={`w-full py-3 rounded-2xl border-2 font-display font-bold text-center transition-all bg-[#12121A] focus:outline-none ${
                      !room?.infinite && !timeOpts.find(o => o.val === currentTurnSeconds)
                        ? "border-[#00FFFF] text-[#00FFFF] bg-[#00FFFF]/10" 
                        : "border-white/10 text-[#A0A0B0] focus:border-white/30"
                    } ${!isHost ? "cursor-default opacity-80" : ""}`}
                  />
                </div>
              </div>

              <div ref={supportRef}>
                <SupportPanel code={room?.code} onRoomUpdate={actions.updateRoom} adFreeUntil={room?.ad_free_until} />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] flex items-center gap-2">
                  <Vote size={14} /> {t("vote_sets")}
                </h2>
                <button onClick={() => setCreating((v) => !v)} className="flex items-center gap-1 text-sm font-bold text-[#00FFFF] bg-[#00FFFF]/10 px-3 py-1.5 rounded-lg border border-[#00FFFF]/30 hover:bg-[#00FFFF]/20 transition-colors">
                  <Plus size={16} /> {t("custom_set")}
                </button>
              </div>

              <div className="bg-[#12121A] p-3 sm:p-4 rounded-2xl border border-white/5 mb-6 shadow-lg">
                 <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A0A0B0]" size={20} />
                    <input 
                        type="text" 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("search_category") || "Szukaj..."} 
                        className="w-full bg-[#050510] border-2 border-white/10 rounded-xl py-3 pl-10 pr-10 text-white placeholder:text-[#A0A0B0] focus:outline-none focus:border-[#00FFFF] transition-colors"
                    />
                    {searchQuery && (
                        <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0A0B0] hover:text-white transition-colors">
                            <X size={18} />
                        </button>
                    )}
                 </div>

                 <div className="max-h-[400px] overflow-y-auto pr-2 custom-scrollbar flex flex-col gap-2">
                    {!searchQuery.trim() ? (
                        <>
                            <div className="text-[10px] font-mono2 tracking-[0.2em] text-[#FFE800] mb-1 uppercase flex items-center gap-2">
                                <Sparkles size={12} /> {t("popular_this_week") || "Popularne w tym tygodniu"}
                            </div>
                            {popularDecks.map(deck => (
                                <FlatDeckCard key={deck.id} deck={deck} room={room} playerId={playerId} actions={actions} addingSets={addingSets} setAddingSets={setAddingSets} validSets={validSets} />
                            ))}
                        </>
                    ) : searchResults.length > 0 ? (
                        <>
                            <div className="text-[10px] font-mono2 tracking-[0.2em] text-[#00FFFF] mb-1 uppercase">
                                {t("search_results") || "Wyniki wyszukiwania"} ({searchResults.length})
                            </div>
                            {searchResults.map(deck => (
                                <FlatDeckCard key={deck.id} deck={deck} room={room} playerId={playerId} actions={actions} addingSets={addingSets} setAddingSets={setAddingSets} validSets={validSets} />
                            ))}
                        </>
                    ) : (
                        <div className="text-center py-10 text-[#A0A0B0] text-sm">
                            {t("no_results_for") || "Brak wyników dla"} <strong className="text-white">"{searchQuery}"</strong>.<br /> {t("check_typos") || "Sprawdź literówki!"}
                        </div>
                    )}
                 </div>
              </div>

              {creating && (
                  <div className="mb-6 p-5 rounded-2xl bg-[#12121A] border-2 border-[#B26DFF]/50 pop-in space-y-4 shadow-[0_0_30px_rgba(178,109,255,0.1)]">
                      <div className="flex items-center gap-2 text-[#B26DFF] font-bold mb-2">
                         <Save size={18} /> {t("custom_set")}
                      </div>
                      <input value={setName} onChange={(e) => setSetName(e.target.value)} placeholder={t("set_name_ph")} className="w-full bg-[#050510] border-2 border-white/10 rounded-xl py-3 px-4 focus:outline-none focus:border-[#B26DFF]" />
                      
                      <div className="space-y-2 max-h-[200px] overflow-y-auto pr-2 custom-scrollbar">
                        {cards.map((c, i) => (
                            <input key={c._id} value={c.text} onChange={(e) => updateCard(i, "text", e.target.value)} placeholder={t("card_name_ph", { n: i + 1 })} className="w-full bg-[#050510] border-2 border-white/10 rounded-xl py-2.5 px-3 text-sm focus:outline-none focus:border-[#B26DFF]" />
                        ))}
                      </div>

                      <div className="flex gap-2 pt-2">
                          <button onClick={() => setCards((cs) => [...cs, newCard()])} className="px-4 py-2.5 rounded-xl border-2 border-white/15 text-sm font-bold flex items-center justify-center hover:bg-white/5 transition-colors">
                            <Plus size={20} />
                          </button>
                          <button onClick={saveSet} className="flex-1 py-2.5 rounded-xl bg-[#B26DFF] text-white font-bold text-sm hover:scale-[1.02] transition-transform">
                             {t("save_set")}
                          </button>
                      </div>
                  </div>
              )}

              {savedLocalSets.length > 0 && (
                <div className="mt-8 mb-6">
                  <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#B26DFF] mb-3 flex items-center gap-2">
                    <Sparkles size={14} /> {t("custom_set")}
                  </h2>
                  <div className="space-y-2">
                    {savedLocalSets.map(set => {
                       const isLoadedInRoom = validSets.some(s => s.name === set.name);
                       return (
                         <div key={set.id} className="flex items-center justify-between p-3.5 bg-[#12121A] border-2 border-white/10 rounded-xl">
                            <div>
                               <div className="font-bold text-white text-sm">{set.name}</div>
                               <div className="text-xs text-[#A0A0B0]">{safeArray(set?.cards).length} {t("cards_word") || "kart"}</div>
                            </div>
                            <div className="flex items-center gap-2">
                               {!isLoadedInRoom ? (
                                 <button onClick={() => { actions.addSet(set.name, set.cards).then(() => actions.vote(set.name)); toast.success(t("custom_added") || "Dodano do gry!"); }} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold transition-colors flex items-center gap-1">
                                    <Download size={14} /> {t("join") || "Dodaj"}
                                 </button>
                               ) : (
                                 <span className="px-3 py-1.5 bg-[#00FF66]/20 text-[#00FF66] rounded-lg text-xs font-bold flex items-center gap-1">
                                    <Check size={14} /> {t("in_pool") || "W grze"}
                                 </span>
                               )}
                               <button onClick={() => deleteLocalSet(set.id)} className="p-1.5 text-[#505060] hover:text-[#FF007F] transition-colors rounded-lg">
                                  <Trash2 size={16} />
                               </button>
                            </div>
                         </div>
                       )
                    })}
                  </div>
                </div>
              )}

              {customSets.length > 0 && (
                <div className="mt-8">
                  <h2 className="font-mono2 text-xs tracking-[0.3em] uppercase text-[#A0A0B0] mb-3">{t("in_pool") || "Zestawy w pokoju"}</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {customSets.map((s) => (
                      <VoteCard key={s.id} s={s} room={room} playerId={playerId} actions={actions} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="mt-10 pb-10">
             {isHost ? (
              <button onClick={handleStartGame} disabled={!canStart} className="w-full min-h-[68px] py-2 rounded-2xl bg-[#00FF66] text-[#050510] font-display font-black text-xl flex flex-col items-center justify-center gap-1 disabled:opacity-40 disabled:hover:translate-y-0 transition-transform duration-150 hover:-translate-y-1 hard-shadow-white">
                 <div className="flex items-center gap-3"><Play size={26} /> {t("start_game")}</div>
              </button>
             ) : (
               <div className="text-center py-6 text-[#A0A0B0] glow-pulse font-mono2 tracking-widest">{t("waiting_host")}</div>
             )}
          </div>
        </div>
        {showAds && (
          <div className="hidden lg:block w-48 xl:w-64">
            <div className="sticky top-4 h-[calc(100vh-32px)]"><GoogleAd format="vertical" minHeight={600} label="ad" className="h-full w-full" /></div>
          </div>
        )}
      </div>
      <div className="w-full z-20 relative"><LegalFooter screen="LOBBY" /></div>
    </div>
  );
}